import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, User, BookText, ClipboardList, ShieldAlert, Pill, ListChecks,
  ClipboardCheck, Activity, Heart, FileCheck, PenSquare, Loader2, Save,
  Trash2, Plus, CheckCircle2, Menu, X, AlertCircle, Mic, FileDown,
  MessageSquare, Send, ChevronRight, Gavel, Sparkles,
} from "lucide-react";
import PkiVoiceRecorder from "../../components/pflege-ki/PkiVoiceRecorder";
import MdSimulationModal from "../../components/pflege-ki/MdSimulationModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PatientenmappeDetail — 11-Sektionen-Sidebar-Layout im Paradygma-Stil.
 * URL: /campus/app/patientenmappe/:id
 * Sektionen werden per PATCH mit Debounce (600ms) auto-gespeichert.
 */

const SECTIONS = [
  { id: "stammdaten",       label: "Stammdaten",              short: "1",  icon: User,           group: "Person" },
  { id: "biografie",        label: "Biografie",               short: "2",  icon: BookText,       group: "Person" },
  { id: "sis",              label: "SIS (Themenfelder 1-6)",  short: "3",  icon: ClipboardList,  group: "Assessment" },
  { id: "risikomatrix",     label: "Risikomatrix",            short: "4",  icon: ShieldAlert,    group: "Assessment" },
  { id: "medikation",       label: "Medikation",              short: "5",  icon: Pill,           group: "Planung" },
  { id: "massnahmenplan",   label: "Maßnahmenplan",           short: "6",  icon: ListChecks,     group: "Planung" },
  { id: "versorgungsauftrag", label: "Versorgungsauftrag",    short: "7",  icon: FileCheck,      group: "Planung" },
  { id: "beobachtung_360",  label: "360°-Beobachtung",        short: "8",  icon: Activity,       group: "Verlauf" },
  { id: "vitalwerte",       label: "Vitalwerte",              short: "9",  icon: Heart,          group: "Verlauf" },
  { id: "qpr26",            label: "QPR-26 Prüfbogen",        short: "10", icon: ClipboardCheck, group: "Qualität" },
  { id: "freigaben",        label: "Freigaben & Signaturen",  short: "11", icon: PenSquare,      group: "Qualität" },
  { id: "kommentare",       label: "Team-Chat",               short: "12", icon: MessageSquare,  group: "Team" },
];

const GROUP_ORDER = ["Person", "Assessment", "Planung", "Verlauf", "Qualität", "Team"];

export default function PatientenmappeDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [mappe, setMappe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("stammdaten");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showMdSim, setShowMdSim] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/patientenmappe/${id}`, { withCredentials: true });
      setMappe(data);
    } catch (e) {
      toast.error("Mappe konnte nicht geladen werden.");
      nav("/campus/app/patientenmappe");
    } finally { setLoading(false); }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  const patchSection = useCallback(async (sectionId, patch) => {
    try {
      const { data } = await axios.patch(
        `${API}/patientenmappe/${id}/section/${sectionId}`,
        { data: patch },
        { withCredentials: true },
      );
      setMappe((prev) => prev ? {
        ...prev,
        sections: { ...prev.sections, [sectionId]: data.data },
        updated_at: data.updated_at,
      } : prev);
      return true;
    } catch (e) {
      toast.error("Speichern fehlgeschlagen.");
      return false;
    }
  }, [id]);

  if (loading || !mappe) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center">
        <div className="text-sand-600 text-[14px] flex items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin" /> Lade Mappe …
        </div>
      </div>
    );
  }

  const stamm = mappe.sections.stammdaten || {};
  const fullName = `${stamm.vorname || ""} ${stamm.nachname || ""}`.trim() || mappe.label;
  const activeMeta = SECTIONS.find((s) => s.id === active);

  // Fortschritt: Wie viele der 12 Sektionen enthalten mind. 1 nicht-leeres Feld?
  const isSectionFilled = (sid) => {
    const d = mappe.sections?.[sid];
    if (!d) return false;
    if (Array.isArray(d)) return d.length > 0;
    if (typeof d === "object") {
      return Object.values(d).some((v) => {
        if (v == null || v === "") return false;
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === "object") return Object.keys(v).length > 0;
        return true;
      });
    }
    return !!d;
  };
  const filledCount = SECTIONS.filter((s) => isSectionFilled(s.id)).length;
  const totalCount = SECTIONS.length;
  const progressPct = Math.round((filledCount / totalCount) * 100);

  return (
    <div data-testid="patientenmappe-detail" className="min-h-screen bg-cream-50 flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && <div className="fixed inset-0 z-30 bg-espresso-deep/50 md:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside data-testid="patientenmappe-sidebar" className={`fixed md:sticky top-0 left-0 z-40 md:z-0 h-screen w-72 shrink-0 bg-white border-r border-cream-300 overflow-y-auto transform transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}>
        <div className="p-5 border-b border-cream-300">
          <Link to="/campus/app/patientenmappe" className="inline-flex items-center gap-1.5 text-[12px] text-sand-500 hover:text-brand-darkred mb-3">
            <ArrowLeft className="h-3 w-3" /> Alle Mappen
          </Link>
          <div className="font-display text-xl text-sand-800 leading-tight truncate">{fullName}</div>
          <div className="text-[11.5px] text-sand-500 mt-0.5 truncate">
            {stamm.geburtsdatum && `geb. ${stamm.geburtsdatum}`}
            {stamm.pflegegrad != null && <> · PG {stamm.pflegegrad}</>}
          </div>
          {mappe.is_demo && (
            <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-saffron/40 text-brand-darkred text-[10px] font-bold tracking-wider px-2 py-0.5">
              BEISPIEL-MAPPE
            </div>
          )}
          <a
            href={`${API}/patientenmappe/${id}/pdf`}
            target="_blank" rel="noreferrer"
            data-testid="btn-download-pdf"
            className="mt-3 inline-flex items-center justify-center gap-1.5 w-full rounded-full bg-brand-darkred text-cream-50 px-3 py-2 text-[12.5px] font-semibold hover:bg-brand-red transition-colors">
            <FileDown className="h-3.5 w-3.5" /> Komplette Mappe · PDF
          </a>
          <button
            onClick={() => setShowMdSim(true)}
            data-testid="btn-open-md-simulation"
            className="mt-2 inline-flex items-center justify-center gap-1.5 w-full rounded-full bg-saffron text-brand-darkred px-3 py-2 text-[12.5px] font-semibold hover:bg-saffron-dark transition-colors">
            <Gavel className="h-3.5 w-3.5" /> MD-Prüfsimulation
          </button>
        </div>

        <nav className="p-3 space-y-4">
          {GROUP_ORDER.map((grp) => (
            <div key={grp}>
              <div className="px-3 text-[10px] font-bold uppercase tracking-widest text-sand-500 mb-1.5">{grp}</div>
              <ul className="space-y-0.5">
                {SECTIONS.filter((s) => s.group === grp).map((s) => {
                  const Icon = s.icon;
                  const isActive = active === s.id;
                  const isFilled = isSectionFilled(s.id);
                  return (
                    <li key={s.id}>
                      <button
                        data-testid={`sidebar-btn-${s.id}`}
                        onClick={() => { setActive(s.id); setSidebarOpen(false); }}
                        className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13.5px] text-left transition-colors ${
                          isActive
                            ? "bg-brand-darkred text-cream-50 font-semibold"
                            : "text-sand-700 hover:bg-cream-100"
                        }`}>
                        <span className={`text-[10px] font-bold tabular-nums w-4 shrink-0 ${isActive ? "text-cream-200" : "text-sand-400"}`}>{s.short}</span>
                        <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.8} />
                        <span className="flex-1 truncate">{s.label}</span>
                        {/* Fill-Indikator · grün = ausgefüllt, sand = leer */}
                        <span
                          data-testid={`sidebar-fill-${s.id}`}
                          aria-label={isFilled ? "ausgefüllt" : "leer"}
                          className={`h-2 w-2 shrink-0 rounded-full transition-colors ${
                            isFilled
                              ? (isActive ? "bg-emerald-300" : "bg-emerald-500")
                              : (isActive ? "bg-cream-100/25" : "bg-sand-200")
                          }`}
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        {/* Mobile Header · unter der globalen CampusLayout-TopBar (h-14) */}
        <div className="md:hidden sticky top-14 z-20 bg-white/95 backdrop-blur border-b border-cream-300 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} className="p-1 -ml-1 text-sand-800 inline-flex items-center gap-1.5 text-[12px] font-semibold" aria-label="Sektionen" data-testid="btn-open-sections">
            <Menu className="h-5 w-5" strokeWidth={1.8} />
            <span className="text-sand-600">Sektionen</span>
          </button>
          <div className="text-[13px] font-semibold text-sand-800 truncate mx-3">{activeMeta?.label}</div>
          <div className="w-16" />
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-6 sm:py-10 pb-24 md:pb-10">
          {/* Sektions-Fortschritt · immer sichtbar oben */}
          <div
            data-testid="patientenmappe-progress"
            className="mb-6 rounded-xl border border-cream-300 bg-white px-4 py-3"
          >
            <div className="flex items-center justify-between text-[12.5px] mb-2">
              <span className="font-semibold text-sand-700">
                <span className="text-brand-darkred tabular-nums">{filledCount}</span> von {totalCount} Sektionen ausgefüllt
              </span>
              <span className="text-sand-500 tabular-nums font-semibold">{progressPct}%</span>
            </div>
            <div className="h-2 w-full rounded-full bg-cream-200 overflow-hidden">
              <div
                className="h-full bg-brand-darkred transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          <div className="hidden md:flex items-center gap-2 text-[11px] uppercase tracking-widest text-sand-500 mb-2">
            <span className="text-brand-darkred">{activeMeta?.short}</span>
            <span>·</span>
            <span>{activeMeta?.group}</span>
          </div>
          <h2 className="font-display text-3xl text-sand-800 leading-tight mb-6">{activeMeta?.label}</h2>

          <SectionRenderer sectionId={active} mappe={mappe} onPatch={patchSection} onReload={load} mid={id} />
        </div>
      </div>

      {showMdSim && (
        <MdSimulationModal
          mid={id}
          patientName={fullName}
          onClose={() => setShowMdSim(false)}
        />
      )}
    </div>
  );
}

/* ============================================================
 * SECTION RENDERER — dispatch to specific section editors
 * ============================================================ */
function SectionRenderer({ sectionId, mappe, onPatch, onReload, mid }) {
  const data = mappe.sections[sectionId] || {};
  switch (sectionId) {
    case "stammdaten":         return <StammdatenForm data={data} onPatch={(p) => onPatch("stammdaten", p)} />;
    case "biografie":          return <BiografieForm data={data} onPatch={(p) => onPatch("biografie", p)} />;
    case "sis":                return <SisForm data={data} onPatch={(p) => onPatch("sis", p)} />;
    case "risikomatrix":       return <RisikoForm data={data} onPatch={(p) => onPatch("risikomatrix", p)} />;
    case "medikation":         return <ListSection kind="medikation" mid={mid} data={data} onReload={onReload} />;
    case "massnahmenplan":     return <ListSection kind="massnahmenplan" mid={mid} data={data} onReload={onReload} />;
    case "beobachtung_360":    return <ListSection kind="beobachtung_360" mid={mid} data={data} onReload={onReload} />;
    case "vitalwerte":         return <ListSection kind="vitalwerte" mid={mid} data={data} onReload={onReload} />;
    case "versorgungsauftrag": return <VersorgungForm data={data} onPatch={(p) => onPatch("versorgungsauftrag", p)} />;
    case "qpr26":              return <QprForm data={data} onPatch={(p) => onPatch("qpr26", p)} />;
    case "freigaben":          return <FreigabenPanel mid={mid} data={data} onReload={onReload} />;
    case "kommentare":         return <KommentarePanel mid={mid} data={data} onReload={onReload} />;
    default:                   return <div className="text-sand-600 text-[14px]">Sektion nicht gefunden.</div>;
  }
}

/* -------- Auto-Save Hook ------- */
function useAutoSave(initial, onPatch, delay = 600) {
  const [local, setLocal] = useState(initial);
  const [status, setStatus] = useState("idle"); // idle | dirty | saving | saved
  const timerRef = useRef(null);
  const initialRef = useRef(initial);

  // Wenn parent-data von außen wechselt (Reload), sync
  useEffect(() => {
    initialRef.current = initial;
    setLocal(initial);
  }, [initial]);

  const update = (patch) => {
    setLocal((prev) => ({ ...prev, ...patch }));
    setStatus("dirty");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setStatus("saving");
      const ok = await onPatch(patch);
      setStatus(ok ? "saved" : "idle");
      if (ok) setTimeout(() => setStatus("idle"), 1500);
    }, delay);
  };

  return [local, update, status];
}

function SaveBadge({ status }) {
  if (status === "dirty") return <span className="text-[11px] text-sand-500">ungespeichert …</span>;
  if (status === "saving") return <span className="text-[11px] text-sand-600 inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> speichere …</span>;
  if (status === "saved") return <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> gespeichert</span>;
  return <span className="text-[11px] text-sand-400 inline-flex items-center gap-1"><Save className="h-3 w-3" /> Auto-Save aktiv</span>;
}

/* ============================================================
 * Sektions-Editoren
 * ============================================================ */

const INPUT = "w-full rounded-lg border border-cream-300 bg-white px-3 py-2 text-[14px] text-sand-800 focus:outline-none focus:border-brand-darkred/60 focus:ring-2 focus:ring-brand-darkred/10";
const LABEL = "block text-[11.5px] font-semibold uppercase tracking-wider text-sand-600 mb-1";
const TEXTAREA = INPUT + " min-h-[80px] leading-relaxed";

function Row({ children, cols = 2 }) {
  const cls = cols === 3 ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2";
  return <div className={`grid ${cls} gap-3`}>{children}</div>;
}
function Field({ label, children }) { return <label className="block"><span className={LABEL}>{label}</span>{children}</label>; }

function SectionCard({ title, hint, status, children }) {
  return (
    <div className="rounded-2xl bg-white border border-cream-300 p-5 sm:p-6 mb-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-semibold text-sand-800 text-[15px]">{title}</div>
          {hint && <div className="text-[12px] text-sand-500 mt-0.5">{hint}</div>}
        </div>
        {status && <SaveBadge status={status} />}
      </div>
      {children}
    </div>
  );
}

/* --- 1 Stammdaten --- */
function StammdatenForm({ data, onPatch }) {
  const [local, update, status] = useAutoSave(data, onPatch);
  const nested = (key, sub, val) => update({ [key]: { ...(local[key] || {}), [sub]: val } });

  return (
    <>
      <SectionCard title="Person" status={status}>
        <Row cols={3}>
          <Field label="Vorname"><input className={INPUT} value={local.vorname || ""} onChange={(e) => update({ vorname: e.target.value })} data-testid="stamm-vorname" /></Field>
          <Field label="Nachname"><input className={INPUT} value={local.nachname || ""} onChange={(e) => update({ nachname: e.target.value })} data-testid="stamm-nachname" /></Field>
          <Field label="Geburtsdatum"><input type="date" className={INPUT} value={local.geburtsdatum || ""} onChange={(e) => update({ geburtsdatum: e.target.value })} /></Field>
        </Row>
        <div className="mt-3"><Row cols={3}>
          <Field label="Geschlecht"><select className={INPUT} value={local.geschlecht || ""} onChange={(e) => update({ geschlecht: e.target.value })}>
            <option value="">—</option><option value="weiblich">weiblich</option><option value="männlich">männlich</option><option value="divers">divers</option>
          </select></Field>
          <Field label="Pflegegrad"><select className={INPUT} value={local.pflegegrad ?? ""} onChange={(e) => update({ pflegegrad: e.target.value ? Number(e.target.value) : null })}>
            <option value="">—</option>{[0, 1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n === 0 ? "ohne" : `PG ${n}`}</option>)}
          </select></Field>
          <Field label="Telefon"><input className={INPUT} value={local.telefon || ""} onChange={(e) => update({ telefon: e.target.value })} /></Field>
        </Row></div>
      </SectionCard>

      <SectionCard title="Anschrift">
        <Row cols={3}>
          <Field label="Straße / Nr."><input className={INPUT} value={local.strasse || ""} onChange={(e) => update({ strasse: e.target.value })} /></Field>
          <Field label="PLZ"><input className={INPUT} value={local.plz || ""} onChange={(e) => update({ plz: e.target.value })} /></Field>
          <Field label="Ort"><input className={INPUT} value={local.ort || ""} onChange={(e) => update({ ort: e.target.value })} /></Field>
        </Row>
      </SectionCard>

      <SectionCard title="Kostenträger & Arzt">
        <Row>
          <Field label="Krankenkasse"><input className={INPUT} value={local.krankenkasse || ""} onChange={(e) => update({ krankenkasse: e.target.value })} /></Field>
          <Field label="Versichertennummer"><input className={INPUT} value={local.versichertennummer || ""} onChange={(e) => update({ versichertennummer: e.target.value })} /></Field>
        </Row>
        <div className="mt-3"><Field label="Hausarzt"><input className={INPUT} value={local.hausarzt || ""} onChange={(e) => update({ hausarzt: e.target.value })} /></Field></div>
      </SectionCard>

      <SectionCard title="Notfallkontakt">
        <Row cols={3}>
          <Field label="Name"><input className={INPUT} value={local.notfallkontakt?.name || ""} onChange={(e) => nested("notfallkontakt", "name", e.target.value)} /></Field>
          <Field label="Telefon"><input className={INPUT} value={local.notfallkontakt?.telefon || ""} onChange={(e) => nested("notfallkontakt", "telefon", e.target.value)} /></Field>
          <Field label="Beziehung"><input className={INPUT} value={local.notfallkontakt?.beziehung || ""} onChange={(e) => nested("notfallkontakt", "beziehung", e.target.value)} /></Field>
        </Row>
      </SectionCard>

      <SectionCard title="Betreuung / Vollmacht">
        <Row cols={3}>
          <Field label="Name"><input className={INPUT} value={local.betreuung?.name || ""} onChange={(e) => nested("betreuung", "name", e.target.value)} /></Field>
          <Field label="Telefon"><input className={INPUT} value={local.betreuung?.telefon || ""} onChange={(e) => nested("betreuung", "telefon", e.target.value)} /></Field>
          <Field label="Art"><input className={INPUT} value={local.betreuung?.typ || ""} onChange={(e) => nested("betreuung", "typ", e.target.value)} placeholder="Vorsorgevollmacht / Betreuung" /></Field>
        </Row>
      </SectionCard>
    </>
  );
}

/* --- 2 Biografie --- */
function BiografieForm({ data, onPatch }) {
  const [local, update, status] = useAutoSave(data, onPatch);
  const fields = [
    ["lebensgeschichte", "Lebensgeschichte", "Kurzer Lebenslauf, wichtige Stationen"],
    ["beruf", "Beruf / Ausbildung", "Beruflicher Werdegang, ggf. Ruhestand"],
    ["familie", "Familie & Beziehungen", "Angehörige, wichtige Bezugspersonen"],
    ["gewohnheiten", "Gewohnheiten & Tagesstruktur", "Aufsteh- und Ruhezeiten, Rituale"],
    ["vorlieben", "Vorlieben & Interessen", "Musik, Lektüre, Hobbys"],
    ["aengste", "Ängste & Belastungen", "Was macht Angst oder Sorgen?"],
    ["wuensche_am_lebensende", "Wünsche am Lebensende", "Patientenverfügung, Bestattung, Rituale"],
  ];
  return (
    <SectionCard title="Biografie & Lebenswelt" hint="Alles was hilft, den Menschen zu verstehen." status={status}>
      <div className="space-y-3">
        {fields.map(([k, label, hint]) => (
          <Field key={k} label={label}>
            <textarea className={TEXTAREA} placeholder={hint} value={local[k] || ""} onChange={(e) => update({ [k]: e.target.value })} data-testid={`bio-${k}`} />
          </Field>
        ))}
      </div>
    </SectionCard>
  );
}

/* --- 3 SIS (Paradygma-Struktur: Leitfragen + 6 Themenfelder × 3 Sub-Felder) --- */
const SIS_TF = [
  { key: "tf1_kognition",       chip: "TF 1", label: "Kognitive und kommunikative Fähigkeiten" },
  { key: "tf2_mobilitaet",      chip: "TF 2", label: "Mobilität und Beweglichkeit" },
  { key: "tf3_krankheit",       chip: "TF 3", label: "Krankheitsbezogene Anforderungen und Belastungen" },
  { key: "tf4_selbstversorgung", chip: "TF 4", label: "Selbstversorgung" },
  { key: "tf5_soziales",        chip: "TF 5", label: "Leben in sozialen Beziehungen" },
  { key: "tf6_wohnen",          chip: "TF 6", label: "Wohnen und Häuslichkeit" },
];

const SIS_CHIPS = {
  tf1_kognition: ["Zeitlich orientiert", "Örtlich unsicher", "Demenz diagnostiziert", "Vergesslichkeit", "Wortfindungsstörungen", "Hörgerät", "Brille", "Versteht Aufforderungen", "Kann Bedürfnisse äußern", "Nächtliche Unruhe"],
  tf2_mobilitaet: ["Rollator", "Rollstuhl", "Gehstock", "Sturz in letzten 12 Monaten", "Transferhilfe nötig", "Treppen nicht möglich", "Bettlägerig", "Geht sicher in der Wohnung", "Kraftminderung", "Schwindel"],
  tf3_krankheit: ["Schmerzen", "Diabetes", "Herzinsuffizienz", "Wunde / Dekubitus", "Atemnot", "Nimmt Medikamente selbst", "Medikamentengabe nötig", "Kompressionsstrümpfe", "Injektionen", "Blutdruckschwankungen"],
  tf4_selbstversorgung: ["Körperpflege mit Hilfe", "Duschen nur mit Hilfe", "Kleidet sich selbst an", "Hilfe beim Ankleiden", "Isst selbstständig", "Hilfe bei Mahlzeiten", "Schluckstörung", "Inkontinenz", "Toilettengang mit Hilfe", "Gewichtsverlust"],
  tf5_soziales: ["Lebt allein", "Partner:in im Haushalt", "Angehörige aktiv", "Wenig soziale Kontakte", "Einsamkeit", "Tagespflege", "Verein / Kirche", "Besuchsdienst", "Feste Tagesstruktur", "Rückzugstendenz"],
  tf6_wohnen: ["Eigene Wohnung", "Barrierefrei", "Stolperfallen", "Treppe im Haus", "Hausnotruf", "Hilfsmittel vorhanden", "Haushaltshilfe nötig", "Einkäufe durch Angehörige", "Haustier", "Haushalt überfordert"],
};

function SisKiPanel({ feld, onResult }) {
  const { id } = useParams();
  const [sel, setSel] = useState([]);
  const [freitext, setFreitext] = useState("");
  const [busy, setBusy] = useState(false);
  const chips = SIS_CHIPS[feld] || [];
  const toggle = (c) => setSel((s) => (s.includes(c) ? s.filter((x) => x !== c) : [...s, c]));

  const generate = async () => {
    if (!sel.length && !freitext.trim()) { toast.error("Bitte Merkmale ankreuzen oder Stichworte eingeben."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/patientenmappe/${id}/sis/generate`,
        { feld, auswahl: sel, freitext },
        { withCredentials: true },
      );
      onResult(data);
      toast.success("KI-Vorschlag eingefügt – bitte fachlich prüfen und ggf. anpassen.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "KI-Generierung fehlgeschlagen.");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-saffron-600/40 bg-cream-100/60 p-4 mb-4" data-testid={`sis-ki-panel-${feld}`}>
      <div className="text-[11.5px] font-bold uppercase tracking-wider text-brand-darkred mb-2">KI-Assistenz: Ankreuzen → ausformulieren</div>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {chips.map((c) => (
            <button key={c} type="button" onClick={() => toggle(c)}
              data-testid={`sis-chip-${feld}-${c.toLowerCase().replace(/[^a-zä-ü0-9]+/g, "-")}`}
              className={`rounded-full border px-2.5 py-1 text-[12px] transition-colors ${sel.includes(c) ? "bg-brand-darkred text-cream-50 border-brand-darkred" : "bg-white text-sand-700 border-cream-300 hover:border-brand-darkred/50"}`}>
              {c}
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <input className={INPUT + " flex-1"} placeholder="Eigene Stichworte (optional), z. B. „seit OP unsicher, Tochter hilft abends“"
          value={freitext} onChange={(e) => setFreitext(e.target.value)} data-testid={`sis-ki-freitext-${feld}`} />
        <button type="button" onClick={generate} disabled={busy}
          data-testid={`sis-ki-generate-${feld}`}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-darkred text-cream-50 px-4 py-2 text-[13px] font-semibold hover:opacity-90 disabled:opacity-50 shrink-0">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {busy ? "Formuliert …" : "Mit KI ausformulieren"}
        </button>
      </div>
      <p className="text-[11.5px] text-sand-500 mt-2">Der Vorschlag ersetzt die Feldinhalte unten und kann frei bearbeitet werden – die fachliche Verantwortung bleibt bei dir.</p>
    </div>
  );
}

function SisForm({ data, onPatch }) {
  const [local, update, status] = useAutoSave(data, onPatch);
  const setNested = (parentKey, subKey, value) => {
    update({ [parentKey]: { ...(local[parentKey] || {}), [subKey]: value } });
  };
  return (
    <>
      <div className="rounded-xl bg-cream-100 border border-cream-300 p-4 mb-5 flex items-start gap-3">
        <ClipboardList className="h-4 w-4 text-brand-darkred shrink-0 mt-0.5" />
        <div className="text-[13px] text-sand-700 leading-relaxed">
          <strong className="text-sand-800">Strukturierte Informationssammlung nach §113b SGB XI.</strong>{" "}
          Jedes Themenfeld hat drei Sub-Bereiche: <em>Ausformulierter Text · Ressourcen · Unterstützungsbedarf.</em>{" "}
          Nutze das Mikrofon-Icon zum Diktieren.
        </div>
      </div>

      {/* Leitfragen-Block */}
      <SectionCard title="Was bewegt Sie im Augenblick? Was brauchen Sie?" hint="Persönliche Sicht der pflegebedürftigen Person" status={status}>
        <div className="space-y-4">
          <TextareaWithVoice
            label="Persönliche Sicht und Wünsche"
            testid="sis-lf-persoenliche-sicht"
            value={local.leitfragen?.persoenliche_sicht || ""}
            onChange={(v) => setNested("leitfragen", "persoenliche_sicht", v)}
            rows={10}
          />
          <TextareaWithVoice
            label="Ausformulierter SIS-Text"
            testid="sis-lf-ausformuliert"
            value={local.leitfragen?.ausformulierter_text || ""}
            onChange={(v) => setNested("leitfragen", "ausformulierter_text", v)}
            rows={10}
          />
          <TextareaWithVoice
            label="Ressourcen und erhaltene Fähigkeiten"
            testid="sis-lf-ressourcen"
            value={local.leitfragen?.ressourcen || ""}
            onChange={(v) => setNested("leitfragen", "ressourcen", v)}
            rows={10}
          />
          <TextareaWithVoice
            label="Unterstützungsbedarf"
            testid="sis-lf-bedarf"
            value={local.leitfragen?.unterstuetzungsbedarf || ""}
            onChange={(v) => setNested("leitfragen", "unterstuetzungsbedarf", v)}
            rows={10}
          />
          <label className="flex items-center gap-2 pt-2 cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 accent-brand-darkred"
              checked={!!local.leitfragen?.einverstaendnis_unterschrift}
              onChange={(e) => setNested("leitfragen", "einverstaendnis_unterschrift", e.target.checked)}
              data-testid="sis-lf-einverstaendnis"
            />
            <span className="text-[13px] text-sand-700">Einverständnis der/des Klient:in liegt unterschrieben vor.</span>
          </label>
        </div>
      </SectionCard>

      {/* 6 Themenfelder */}
      {SIS_TF.map(({ key, chip, label }) => (
        <SectionCard key={key} title={label} hint={chip}>
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center rounded-md bg-brand-darkred text-cream-50 text-[10px] font-bold tracking-wider px-1.5 py-0.5">{chip}</span>
          </div>
          <div className="space-y-4">
            <SisKiPanel
              feld={key}
              onResult={(r) => update({ [key]: {
                ...(local[key] || {}),
                ausformulierter_text: r.ausformulierter_text,
                ressourcen: r.ressourcen,
                unterstuetzungsbedarf: r.unterstuetzungsbedarf,
              } })}
            />
            <TextareaWithVoice
              label="Ausformulierter SIS-Text"
              testid={`sis-${key}-text`}
              value={local[key]?.ausformulierter_text || ""}
              onChange={(v) => setNested(key, "ausformulierter_text", v)}
              rows={10}
            />
            <TextareaWithVoice
              label="Ressourcen und erhaltene Fähigkeiten"
              testid={`sis-${key}-ressourcen`}
              value={local[key]?.ressourcen || ""}
              onChange={(v) => setNested(key, "ressourcen", v)}
              rows={10}
            />
            <TextareaWithVoice
              label="Unterstützungsbedarf"
              testid={`sis-${key}-bedarf`}
              value={local[key]?.unterstuetzungsbedarf || ""}
              onChange={(v) => setNested(key, "unterstuetzungsbedarf", v)}
              rows={10}
            />
          </div>
        </SectionCard>
      ))}

      <SectionCard title="Situationseinschätzung" hint="Fachliche Gesamtbewertung aller Themenfelder">
        <SisKiPanel
          feld="situationseinschaetzung"
          onResult={(r) => update({ situationseinschaetzung: r.text })}
        />
        <TextareaWithVoice
          label=""
          testid="sis-situation"
          value={local.situationseinschaetzung || ""}
          onChange={(v) => update({ situationseinschaetzung: v })}
          rows={10}
        />
      </SectionCard>
    </>
  );
}

/**
 * Textarea mit Voice-Recorder-Icon.
 * onChange erhält den finalen String — bei Voice wird das Transkript ans Ende angehängt.
 */
function TextareaWithVoice({ label, value, onChange, testid, rows = 10, placeholder = "" }) {
  const handleTranscript = (text) => {
    if (!text) return;
    const glue = value && !/\s$/.test(value) ? " " : "";
    onChange((value || "") + glue + text.trim());
    toast.success("Diktat übernommen");
  };
  return (
    <div>
      {label && (
        <div className="flex items-center justify-between mb-1">
          <span className="block text-[11.5px] font-semibold uppercase tracking-wider text-sand-600">{label}</span>
          <PkiVoiceRecorder compact onTranscript={handleTranscript} />
        </div>
      )}
      {!label && (
        <div className="flex justify-end mb-1">
          <PkiVoiceRecorder compact onTranscript={handleTranscript} />
        </div>
      )}
      <textarea
        className={TEXTAREA + " leading-relaxed"}
        style={{ minHeight: `${rows * 22}px` }}
        rows={rows}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
      />
    </div>
  );
}

/* --- 4 Risikomatrix --- */
function RisikoForm({ data, onPatch }) {
  const [local, update, status] = useAutoSave(data, onPatch);
  const risks = [
    ["sturz", "Sturz", "Sturzrisiko nach Downton / Assessment"],
    ["dekubitus", "Dekubitus", "Braden / Waterlow"],
    ["ernaehrung", "Ernährung", "MNA / BMI / Trinkmenge"],
    ["schmerz", "Schmerz", "NRS 0-10 / VAS"],
    ["inkontinenz", "Inkontinenz", "Grad, Frequenz, Hilfsmittel"],
    ["kognition", "Kognition", "MMSE / DemTect"],
  ];
  const nested = (k, sub, val) => update({ [k]: { ...(local[k] || {}), [sub]: val } });

  return (
    <SectionCard title="Risikomatrix" hint="Nur bei Relevanz: Vertiefung + Assessment-Score dokumentieren." status={status}>
      <div className="space-y-2">
        {risks.map(([k, label, hint]) => {
          const r = local[k] || {};
          const active = r.relevant;
          return (
            <div key={k} className={`rounded-xl border p-3 sm:p-4 transition-colors ${active ? "border-brand-darkred/50 bg-brand-rose/10" : "border-cream-300 bg-cream-50"}`}>
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!active} onChange={(e) => nested(k, "relevant", e.target.checked)} className="h-4 w-4 rounded border-cream-300 accent-brand-darkred" data-testid={`risk-${k}-relevant`} />
                  <span className="font-semibold text-sand-800 text-[14px]">{label}</span>
                </label>
                <span className="text-[11.5px] text-sand-500 ml-auto">{hint}</span>
              </div>
              {active && (
                <div className="mt-3 pl-6">
                  <Row cols={3}>
                    <Field label="Score">
                      <input type="number" min="0" max="10" className={INPUT} value={r.score ?? ""} onChange={(e) => nested(k, "score", e.target.value === "" ? null : Number(e.target.value))} data-testid={`risk-${k}-score`} />
                    </Field>
                    <div className="sm:col-span-2"><Field label="Hinweis"><input className={INPUT} value={r.hinweis || ""} onChange={(e) => nested(k, "hinweis", e.target.value)} /></Field></div>
                  </Row>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

/* --- 7 Versorgungsauftrag --- */
function VersorgungForm({ data, onPatch }) {
  const [local, update, status] = useAutoSave(data, onPatch);
  const setLeistung = (idx, key, val) => {
    const arr = [...(local.leistungen || [])];
    arr[idx] = { ...arr[idx], [key]: val };
    update({ leistungen: arr });
  };
  const addLeistung = () => update({ leistungen: [...(local.leistungen || []), { leistung: "", code: "", frequenz_pro_woche: 0, dauer_min: 0 }] });
  const rmLeistung = (idx) => update({ leistungen: (local.leistungen || []).filter((_, i) => i !== idx) });

  return (
    <>
      <SectionCard title="Auftragsrahmen" status={status}>
        <Row>
          <Field label="Kostenträger"><input className={INPUT} value={local.kostentraeger || ""} onChange={(e) => update({ kostentraeger: e.target.value })} /></Field>
          <Field label="Besonderheiten"><input className={INPUT} value={local.besonderheiten || ""} onChange={(e) => update({ besonderheiten: e.target.value })} /></Field>
        </Row>
        <div className="mt-3"><Row>
          <Field label="Auftrag ab"><input type="date" className={INPUT} value={local.auftrag_ab || ""} onChange={(e) => update({ auftrag_ab: e.target.value })} /></Field>
          <Field label="Auftrag bis"><input type="date" className={INPUT} value={local.auftrag_bis || ""} onChange={(e) => update({ auftrag_bis: e.target.value })} /></Field>
        </Row></div>
      </SectionCard>

      <SectionCard title="Leistungen">
        <div className="space-y-2">
          {(local.leistungen || []).map((l, i) => (
            <div key={i} className="rounded-lg border border-cream-300 bg-cream-50 p-3">
              <Row cols={3}>
                <div className="sm:col-span-2"><Field label="Leistung"><input className={INPUT} value={l.leistung || ""} onChange={(e) => setLeistung(i, "leistung", e.target.value)} /></Field></div>
                <Field label="Code"><input className={INPUT} value={l.code || ""} onChange={(e) => setLeistung(i, "code", e.target.value)} /></Field>
              </Row>
              <div className="mt-2"><Row cols={3}>
                <Field label="Frequenz (× / Woche)"><input type="number" className={INPUT} value={l.frequenz_pro_woche || 0} onChange={(e) => setLeistung(i, "frequenz_pro_woche", Number(e.target.value))} /></Field>
                <Field label="Dauer (Min)"><input type="number" className={INPUT} value={l.dauer_min || 0} onChange={(e) => setLeistung(i, "dauer_min", Number(e.target.value))} /></Field>
                <div className="flex items-end"><button onClick={() => rmLeistung(i)} className="text-sand-500 hover:text-brand-darkred p-2" aria-label="Löschen"><Trash2 className="h-4 w-4" /></button></div>
              </Row></div>
            </div>
          ))}
          <button onClick={addLeistung} className="inline-flex items-center gap-1.5 text-[13px] text-brand-darkred font-semibold hover:underline"><Plus className="h-4 w-4" /> Leistung hinzufügen</button>
        </div>
      </SectionCard>
    </>
  );
}

/* --- 5/6/8/9 Listen-Sektionen (Medikation, Maßnahmen, 360°, Vital) --- */
function ListSection({ kind, mid, data, onReload }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(getEmptyEntry(kind));
  const [showForm, setShowForm] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/patientenmappe/${mid}/section/${kind}/entry`, form, { withCredentials: true });
      toast.success("Eintrag gespeichert");
      setForm(getEmptyEntry(kind));
      setShowForm(false);
      onReload();
    } catch (e) { toast.error("Speichern fehlgeschlagen."); }
    finally { setLoading(false); }
  };
  const remove = async (eid) => {
    if (!confirm("Eintrag löschen?")) return;
    try {
      await axios.delete(`${API}/patientenmappe/${mid}/section/${kind}/entry/${eid}`, { withCredentials: true });
      onReload();
    } catch { toast.error("Löschen fehlgeschlagen."); }
  };

  const entries = (data.eintraege || []).slice().reverse();
  const meta = LIST_META[kind];

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-[13px] text-sand-600">{entries.length} Einträge</div>
        <button data-testid={`list-add-${kind}`} onClick={() => setShowForm((v) => !v)} className="inline-flex items-center gap-1.5 rounded-full bg-brand-darkred text-cream-50 px-3 py-1.5 text-[13px] font-semibold hover:bg-brand-red">
          <Plus className="h-3.5 w-3.5" /> {showForm ? "Schließen" : "Neuer Eintrag"}
        </button>
      </div>

      {showForm && (
        <div className="rounded-2xl bg-white border-2 border-brand-darkred/30 p-5 mb-5">
          <form onSubmit={add} className="space-y-3">
            {meta.fields.map(({ key, label, type = "text", cols = 12 }) => (
              <div key={key}>
                <span className={LABEL}>{label}</span>
                {type === "textarea" ? (
                  <textarea className={TEXTAREA} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`list-input-${kind}-${key}`} />
                ) : type === "number" ? (
                  <input type="number" className={INPUT} value={form[key] ?? ""} onChange={(e) => setForm({ ...form, [key]: e.target.value === "" ? "" : Number(e.target.value) })} data-testid={`list-input-${kind}-${key}`} />
                ) : type === "select" ? (
                  <select className={INPUT} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`list-input-${kind}-${key}`}>
                    <option value="">—</option>
                    {(meta.options?.[key] || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type={type} className={INPUT} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`list-input-${kind}-${key}`} />
                )}
              </div>
            ))}
            <button type="submit" data-testid={`list-submit-${kind}`} disabled={loading} className="rounded-full bg-brand-darkred text-cream-50 px-4 py-2 text-[13.5px] font-semibold hover:bg-brand-red disabled:opacity-60 inline-flex items-center gap-2">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Eintrag speichern
            </button>
          </form>
        </div>
      )}

      {entries.length === 0 && (
        <div className="rounded-2xl bg-white border border-cream-300 p-10 text-center text-sand-500 text-[13px]">
          Noch keine Einträge in dieser Sektion.
        </div>
      )}

      <div className="space-y-2">
        {entries.map((e) => (
          <div key={e.entry_id} data-testid={`list-entry-${kind}-${e.entry_id}`} className="rounded-xl bg-white border border-cream-300 p-4 hover:border-brand-darkred/30">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">{meta.render(e)}</div>
              <button onClick={() => remove(e.entry_id)} className="text-sand-400 hover:text-brand-darkred p-1 shrink-0"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

const LIST_META = {
  medikation: {
    fields: [
      { key: "name", label: "Medikament" },
      { key: "wirkstoff", label: "Wirkstoff" },
      { key: "dosis", label: "Dosis" },
      { key: "morgens", label: "Morgens", type: "number" },
      { key: "mittags", label: "Mittags", type: "number" },
      { key: "abends", label: "Abends", type: "number" },
      { key: "nacht", label: "Nacht", type: "number" },
      { key: "bedarf", label: "Bei Bedarf" },
      { key: "hinweis", label: "Hinweis", type: "textarea" },
      { key: "arzt", label: "Verordnender Arzt" },
      { key: "seit", label: "Seit", type: "date" },
    ],
    render: (e) => (
      <>
        <div className="font-semibold text-sand-800 text-[14.5px]">{e.name}</div>
        <div className="text-[12.5px] text-sand-600 mt-0.5">
          {e.dosis} · M {e.morgens || 0} / Mi {e.mittags || 0} / Ab {e.abends || 0} / N {e.nacht || 0}
          {e.bedarf && <> · bei Bedarf: {e.bedarf}</>}
        </div>
        {e.hinweis && <div className="text-[12px] text-sand-500 mt-1">{e.hinweis}</div>}
        {e.arzt && <div className="text-[11px] text-sand-400 mt-1">{e.arzt} · seit {e.seit || "—"}</div>}
      </>
    ),
  },
  massnahmenplan: {
    fields: [
      { key: "titel", label: "Titel" },
      { key: "kategorie", label: "Kategorie", type: "select" },
      { key: "ziel", label: "Ziel", type: "textarea" },
      { key: "massnahme", label: "Maßnahme", type: "textarea" },
      { key: "frequenz", label: "Frequenz" },
      { key: "verantwortlich", label: "Verantwortlich" },
      { key: "evaluation_am", label: "Evaluation am", type: "date" },
    ],
    options: { kategorie: ["Grundpflege", "Behandlungspflege", "Bewegung", "Ernährung", "Psychosozial", "Kommunikation", "Angehörige"] },
    render: (e) => (
      <>
        <div className="flex items-center gap-2 mb-1">
          <div className="font-semibold text-sand-800 text-[14.5px]">{e.titel}</div>
          {e.kategorie && <span className="inline-flex items-center rounded-full bg-cream-100 text-sand-700 text-[10.5px] font-semibold px-2 py-0.5">{e.kategorie}</span>}
        </div>
        {e.ziel && <div className="text-[12.5px] text-sand-700 mt-1"><strong>Ziel:</strong> {e.ziel}</div>}
        {e.massnahme && <div className="text-[12.5px] text-sand-600 mt-1"><strong>Maßnahme:</strong> {e.massnahme}</div>}
        <div className="text-[11.5px] text-sand-500 mt-1.5">
          {e.frequenz && <>Frequenz: {e.frequenz}</>}
          {e.verantwortlich && <> · {e.verantwortlich}</>}
          {e.evaluation_am && <> · Eval: {e.evaluation_am}</>}
        </div>
      </>
    ),
  },
  beobachtung_360: {
    fields: [
      { key: "datum", label: "Datum", type: "date" },
      { key: "kategorie", label: "Kategorie", type: "select" },
      { key: "autor", label: "Autor / Kürzel" },
      { key: "ampel", label: "Ampel", type: "select" },
      { key: "text", label: "Beobachtung", type: "textarea" },
    ],
    options: { kategorie: ["Vitalwert", "Stimmung", "Insulin", "Wunde", "Schmerz", "Sturz", "Kommunikation", "Sonstiges"], ampel: ["gruen", "gelb", "rot"] },
    render: (e) => {
      const bg = e.ampel === "rot" ? "bg-brand-darkred" : e.ampel === "gelb" ? "bg-saffron-dark" : e.ampel === "gruen" ? "bg-emerald-500" : "bg-sand-300";
      return (
        <>
          <div className="flex items-center gap-2 mb-1">
            <span className={`h-2.5 w-2.5 rounded-full ${bg}`} />
            <div className="text-[12.5px] text-sand-700 font-semibold">{e.datum || "—"} · {e.kategorie || "Beobachtung"}</div>
            {e.autor && <div className="text-[11px] text-sand-400 ml-auto">{e.autor}</div>}
          </div>
          <div className="text-[13px] text-sand-800 leading-relaxed">{e.text}</div>
        </>
      );
    },
  },
  vitalwerte: {
    fields: [
      { key: "datum", label: "Datum & Zeit" },
      { key: "rr_sys", label: "RR sys", type: "number" },
      { key: "rr_dia", label: "RR dia", type: "number" },
      { key: "puls", label: "Puls", type: "number" },
      { key: "temperatur", label: "Temp °C", type: "number" },
      { key: "gewicht", label: "Gewicht kg", type: "number" },
      { key: "bz", label: "BZ mg/dl", type: "number" },
      { key: "notiz", label: "Notiz" },
    ],
    render: (e) => (
      <>
        <div className="text-[12.5px] text-sand-700 font-semibold mb-1">{e.datum || "—"}</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[12.5px] text-sand-800">
          {e.rr_sys && <div>RR: <strong>{e.rr_sys}/{e.rr_dia}</strong></div>}
          {e.puls && <div>Puls: <strong>{e.puls}</strong></div>}
          {e.temperatur && <div>Temp: <strong>{e.temperatur} °C</strong></div>}
          {e.gewicht && <div>Gew: <strong>{e.gewicht} kg</strong></div>}
          {e.bz && <div>BZ: <strong>{e.bz} mg/dl</strong></div>}
        </div>
        {e.notiz && <div className="text-[11.5px] text-sand-500 mt-1">{e.notiz}</div>}
      </>
    ),
  },
};

function getEmptyEntry(kind) {
  const empty = {};
  LIST_META[kind]?.fields.forEach((f) => { empty[f.key] = f.type === "number" ? "" : ""; });
  return empty;
}

/* --- 10 QPR-26 (26 Kriterien in 7 Prüfbereichen, dynamisch vom Backend geladen) --- */
const NOTE_META = {
  A: { label: "A · Erfüllt", color: "bg-emerald-500 text-white" },
  B: { label: "B · Weitgehend erfüllt", color: "bg-saffron text-brand-darkred" },
  C: { label: "C · Überwiegend nicht erfüllt", color: "bg-terra text-white" },
  D: { label: "D · Nicht erfüllt", color: "bg-brand-darkred text-cream-50" },
};

function QprForm({ data, onPatch }) {
  const [local, update, status] = useAutoSave(data, onPatch);
  const [katalog, setKatalog] = useState(null);
  const [expandedBereich, setExpandedBereich] = useState(null);

  useEffect(() => {
    axios.get(`${API}/patientenmappe/meta/qpr26`, { withCredentials: true })
      .then((r) => setKatalog(r.data))
      .catch(() => setKatalog({ bereiche: [], count: 0 }));
  }, []);

  const setBewertung = (kn, patch) => {
    const cur = local.bewertungen?.[kn] || { note: "", kommentar: "" };
    update({ bewertungen: { ...(local.bewertungen || {}), [kn]: { ...cur, ...patch } } });
  };

  const totalCount = katalog?.count || 26;
  const stats = { A: 0, B: 0, C: 0, D: 0, offen: 0 };
  const allKriterien = katalog?.bereiche?.flatMap((b) => b.kriterien.map((k) => k.nr)) || [];
  allKriterien.forEach((kn) => {
    const n = local.bewertungen?.[kn]?.note;
    if (n && stats[n] !== undefined) stats[n]++;
    else stats.offen++;
  });

  if (!katalog) {
    return <div className="text-sand-500 text-[13px]"><Loader2 className="h-4 w-4 animate-spin inline mr-1.5" /> Lade QPR-Katalog …</div>;
  }

  return (
    <>
      <SectionCard title={`QPR-26 Prüfbogen · ${totalCount} Kriterien in ${katalog.bereiche.length} Prüfbereichen`} hint="Bewertung A-D nach QPR-Systematik 2026 · Original-Wortlaut GKV-Spitzenverband" status={status}>
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-5">
          {["A", "B", "C", "D"].map((n) => (
            <div key={n} className={`rounded-lg p-2 text-center ${NOTE_META[n].color}`} data-testid={`qpr-stat-${n}`}>
              <div className="text-2xl font-display leading-none">{stats[n]}</div>
              <div className="text-[10px] uppercase tracking-wider mt-0.5">{n}</div>
            </div>
          ))}
          <div className="rounded-lg p-2 text-center bg-cream-100 text-sand-700">
            <div className="text-2xl font-display leading-none">{stats.offen}</div>
            <div className="text-[10px] uppercase tracking-wider mt-0.5">offen</div>
          </div>
        </div>

        {/* Bereiche */}
        <div className="space-y-3">
          {katalog.bereiche.map((bereich) => {
            const isOpen = expandedBereich === bereich.id;
            const kBewertet = bereich.kriterien.filter((k) => local.bewertungen?.[k.nr]?.note).length;
            const progress = bereich.kriterien.length ? Math.round((kBewertet / bereich.kriterien.length) * 100) : 0;
            return (
              <div key={bereich.id} className="rounded-xl border border-cream-300 overflow-hidden">
                <button
                  data-testid={`qpr-bereich-toggle-${bereich.id}`}
                  onClick={() => setExpandedBereich(isOpen ? null : bereich.id)}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${isOpen ? "bg-brand-darkred text-cream-50" : "bg-cream-50 text-sand-800 hover:bg-cream-100"}`}>
                  <span className={`inline-flex items-center justify-center rounded-full h-7 w-7 text-[12px] font-bold shrink-0 ${isOpen ? "bg-cream-50 text-brand-darkred" : "bg-brand-darkred text-cream-50"}`}>
                    {bereich.id}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[13.5px] leading-tight truncate">{bereich.titel}</div>
                    <div className={`text-[11px] mt-0.5 ${isOpen ? "text-cream-200" : "text-sand-500"}`}>
                      {kBewertet}/{bereich.kriterien.length} bewertet · {progress} %
                    </div>
                  </div>
                  <ChevronRight className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                </button>

                {isOpen && (
                  <div className="bg-white p-3 sm:p-4 space-y-2">
                    {bereich.kriterien.map((k) => {
                      const b = local.bewertungen?.[k.nr] || {};
                      return (
                        <div key={k.nr} className="rounded-lg border border-cream-300 bg-cream-50 p-3">
                          <div className="flex items-start gap-3">
                            <span className="inline-flex items-center rounded-md bg-brand-darkred text-cream-50 text-[10px] font-bold tracking-wider px-1.5 py-0.5 shrink-0 mt-0.5">{k.nr}</span>
                            <div className="flex-1 min-w-0">
                              <div className="text-[13.5px] text-sand-800 font-medium leading-snug">{k.text}</div>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {["A", "B", "C", "D"].map((n) => (
                                  <button
                                    key={n} type="button"
                                    onClick={() => setBewertung(k.nr, { note: n })}
                                    data-testid={`qpr-${k.nr}-${n}`}
                                    className={`px-2.5 py-1 rounded-full text-[11.5px] font-semibold transition-colors ${b.note === n ? NOTE_META[n].color : "bg-white border border-cream-300 text-sand-600 hover:border-brand-darkred/40"}`}>
                                    {n}
                                  </button>
                                ))}
                                {b.note && (
                                  <button
                                    onClick={() => setBewertung(k.nr, { note: "" })}
                                    className="px-2.5 py-1 rounded-full text-[11.5px] font-semibold text-sand-500 hover:text-brand-darkred">
                                    ×
                                  </button>
                                )}
                              </div>
                              {b.note && (
                                <input
                                  placeholder="Kommentar / Nachweis"
                                  className={INPUT + " mt-2 text-[12.5px]"}
                                  value={b.kommentar || ""}
                                  onChange={(e) => setBewertung(k.nr, { kommentar: e.target.value })}
                                  data-testid={`qpr-${k.nr}-kommentar`}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </SectionCard>

      <SectionCard title="Gesamtstatus">
        <Row>
          <Field label="Status">
            <select className={INPUT} value={local.gesamt_status || "offen"} onChange={(e) => update({ gesamt_status: e.target.value })} data-testid="qpr-status">
              <option value="offen">Offen</option>
              <option value="in_pruefung">In Prüfung</option>
              <option value="freigegeben">Freigegeben</option>
            </select>
          </Field>
        </Row>
      </SectionCard>
    </>
  );
}

/* --- 12 Team-Kommentare (Chat-Timeline pro Mappe) --- */
function KommentarePanel({ mid, data, onReload }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const entries = (data.eintraege || []).slice().sort((a, b) => (a.created_at > b.created_at ? 1 : -1));

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/patientenmappe/${mid}/kommentar`, { text: text.trim() }, { withCredentials: true });
      setText("");
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kommentar konnte nicht gesendet werden.");
    } finally { setLoading(false); }
  };

  const remove = async (entry_id) => {
    if (!confirm("Kommentar löschen?")) return;
    try {
      await axios.delete(`${API}/patientenmappe/${mid}/kommentar/${entry_id}`, { withCredentials: true });
      toast.success("Kommentar gelöscht");
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen.");
    }
  };

  return (
    <>
      <SectionCard title="Team-Chat · Absprachen zur Mappe" hint="Nachrichten sind für alle Team-Mitglieder mit Zugriff auf diese Mappe sichtbar.">
        {entries.length === 0 && (
          <div className="rounded-xl bg-cream-50 border border-cream-300 p-6 text-center text-sand-500 text-[13px]" data-testid="kommentare-empty">
            Noch keine Nachrichten. Starte die Absprache mit deinem Team.
          </div>
        )}
        {entries.length > 0 && (
          <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1" data-testid="kommentare-liste">
            {entries.map((c) => (
              <div key={c.entry_id} data-testid={`kommentar-${c.entry_id}`} className="rounded-xl bg-cream-50 border border-cream-300 p-3">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-brand-darkred text-cream-50 text-[10px] font-bold shrink-0">
                      {(c.autor || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                    </span>
                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold text-sand-800 truncate">{c.autor}</div>
                      <div className="text-[10.5px] text-sand-500 truncate">
                        {c.autor_role && <>{c.autor_role} · </>}
                        {c.created_at && new Date(c.created_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => remove(c.entry_id)} className="p-1 text-sand-400 hover:text-brand-darkred shrink-0" aria-label="Kommentar löschen" data-testid={`kommentar-delete-${c.entry_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                {c.section_ref && (
                  <div className="inline-flex items-center rounded-full bg-brand-darkred/10 text-brand-darkred text-[10px] font-semibold px-2 py-0.5 mb-1.5">
                    ↳ {c.section_ref.replace("_", " ")}
                  </div>
                )}
                <div className="text-[13.5px] text-sand-800 leading-relaxed whitespace-pre-wrap">{c.text}</div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard title="Neuer Kommentar">
        <form onSubmit={send} className="space-y-3">
          <textarea
            className={TEXTAREA}
            style={{ minHeight: "88px" }}
            placeholder="z. B. Bitte QPR-Kriterium 6.1 noch nachdokumentieren …"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(e); }}
            data-testid="kommentar-input"
          />
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11.5px] text-sand-500">Cmd/Ctrl+Enter zum Senden</div>
            <button type="submit" disabled={!text.trim() || loading} data-testid="kommentar-submit"
              className="inline-flex items-center gap-2 rounded-full bg-brand-darkred text-cream-50 px-4 py-2 text-[13.5px] font-semibold hover:bg-brand-red disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Senden
            </button>
          </div>
        </form>
      </SectionCard>
    </>
  );
}

/* --- 11 Freigaben --- */
function FreigabenPanel({ mid, data, onReload }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasSig, setHasSig] = useState(false);
  const [status, setStatus] = useState(data.status || "entwurf");
  const [rolle, setRolle] = useState("PDL");
  const [kommentar, setKommentar] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { setStatus(data.status || "entwurf"); }, [data.status]);

  const start = (x, y) => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.beginPath(); ctx.moveTo(x, y); setDrawing(true);
  };
  const draw = (x, y) => {
    if (!drawing) return;
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineTo(x, y); ctx.strokeStyle = "#3D3835"; ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.stroke();
    setHasSig(true);
  };
  const end = () => setDrawing(false);
  const clearSig = () => {
    const c = canvasRef.current; if (!c) return;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasSig(false);
  };
  const getPos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches?.[0]; const cx = t ? t.clientX : e.clientX; const cy = t ? t.clientY : e.clientY;
    return { x: (cx - r.left) * (c.width / r.width), y: (cy - r.top) * (c.height / r.height) };
  };

  const submit = async () => {
    setLoading(true);
    try {
      const body = { status, rolle, kommentar };
      if (hasSig && canvasRef.current) body.signature_data_url = canvasRef.current.toDataURL("image/png");
      await axios.post(`${API}/patientenmappe/${mid}/freigabe`, body, { withCredentials: true });
      toast.success("Freigabe aktualisiert");
      clearSig(); setKommentar("");
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler bei der Freigabe."); }
    finally { setLoading(false); }
  };

  return (
    <>
      <SectionCard title="Aktueller Status">
        <div className={`rounded-lg px-4 py-3 font-semibold text-[14px] mb-4 ${
          data.status === "freigegeben" ? "bg-emerald-100 text-emerald-800" :
          data.status === "fachliche_pruefung" ? "bg-saffron/40 text-brand-darkred" :
          "bg-sand-200 text-sand-800"
        }`}>
          Status: {data.status === "entwurf" ? "Entwurf" : data.status === "fachliche_pruefung" ? "Fachliche Prüfung" : "Freigegeben"}
        </div>

        <div className="space-y-3">
          <Row>
            <Field label="Neuer Status">
              <select className={INPUT} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="freigabe-status">
                <option value="entwurf">Entwurf</option>
                <option value="fachliche_pruefung">Fachliche Prüfung</option>
                <option value="freigegeben">Freigegeben</option>
              </select>
            </Field>
            <Field label="Rolle">
              <input className={INPUT} value={rolle} onChange={(e) => setRolle(e.target.value)} placeholder="PDL / Pflegefachkraft" />
            </Field>
          </Row>
          <Field label="Kommentar">
            <textarea className={TEXTAREA} value={kommentar} onChange={(e) => setKommentar(e.target.value)} placeholder="z. B. Nach Rücksprache mit Frau Musterfrau und Angehörigen …" />
          </Field>

          <div>
            <span className={LABEL}>Signatur (optional)</span>
            <div className="rounded-lg border-2 border-dashed border-cream-300 bg-cream-100 p-2">
              <canvas
                ref={canvasRef}
                width={600} height={140}
                className="w-full h-32 bg-white rounded touch-none cursor-crosshair"
                onMouseDown={(e) => { const p = getPos(e); start(p.x, p.y); }}
                onMouseMove={(e) => { const p = getPos(e); draw(p.x, p.y); }}
                onMouseUp={end} onMouseLeave={end}
                onTouchStart={(e) => { e.preventDefault(); const p = getPos(e); start(p.x, p.y); }}
                onTouchMove={(e) => { e.preventDefault(); const p = getPos(e); draw(p.x, p.y); }}
                onTouchEnd={end}
                data-testid="freigabe-signature-canvas"
              />
              <div className="mt-2 flex items-center justify-between">
                <button onClick={clearSig} className="text-[11.5px] text-sand-600 hover:text-brand-darkred">Signatur löschen</button>
                <span className="text-[11.5px] text-sand-500">{hasSig ? "✓ signiert" : "Mit Finger oder Maus unterschreiben"}</span>
              </div>
            </div>
          </div>

          <button onClick={submit} disabled={loading} data-testid="btn-freigabe-submit"
            className="w-full rounded-full bg-brand-darkred text-cream-50 py-3 text-[14px] font-semibold hover:bg-brand-red disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            <PenSquare className="h-4 w-4" /> Freigabe speichern
          </button>
        </div>
      </SectionCard>

      {data.signaturen?.length > 0 && (
        <SectionCard title="Unterschriften">
          <div className="space-y-3">
            {data.signaturen.map((s, i) => (
              <div key={i} className="rounded-lg border border-cream-300 bg-cream-50 p-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[13.5px] font-semibold text-sand-800">{s.name}</div>
                  <div className="text-[11px] text-sand-500">{s.rolle}</div>
                </div>
                <div className="text-[11px] text-sand-500 mb-2">{s.datum && new Date(s.datum).toLocaleString("de-DE")}</div>
                {s.signature_data_url && <img alt="Signatur" src={s.signature_data_url} className="max-h-16 bg-white rounded" />}
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {data.historie?.length > 0 && (
        <SectionCard title="Historie">
          <ul className="space-y-1.5 text-[12.5px] text-sand-700">
            {data.historie.slice().reverse().map((h, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-sand-400 shrink-0">{h.datum && new Date(h.datum).toLocaleString("de-DE")}</span>
                <span className="font-semibold">{h.aktion}</span>
                <span className="text-sand-600">· {h.user}</span>
                {h.kommentar && <span className="text-sand-500 italic">— {h.kommentar}</span>}
              </li>
            ))}
          </ul>
        </SectionCard>
      )}
    </>
  );
}
